import React, { useEffect, useState, useRef, useMemo } from 'react';
import { BubbleInput, BubbleButton, BubbleAlert } from '@go-client/components/BubbleUI';
import { safeIdCompare, hasOwnProperty } from '@go-shared/lib/utils';
import { mapBy } from '@go-shared/lib/functional';
import {
  DayThemeTranslation,
  DayThemeTransforms,
  DayThemeTranslatable,
} from '@go-shared/types/translation';
import { replaceTranslationByRefer, getTranslations } from '@go-client/lib/api/translation';
import { RouteComponentProps, Prompt } from 'react-router-dom';
import Fuse from 'fuse.js';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import TranslationBlock from '@go-client/components/TranslationBlock';
import Translations from './Translations';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  generateDefaultNewTranslation,
  makeDayThemeTranslationTransform,
  TranslationFilters,
} from '@go-shared/lib/translation';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BrandDayTheme } from '@go-shared/types/brand';
import TranslationsSubNavigation from '../../components/Navigations/TranslationsSubNavigation';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { canModifyBrandTranslation } from '@go-shared/lib/permissions';

interface DayThemeTranslationDataBundle {
  dayThemes: BrandDayTheme[];
  dayThemeTranslations: DayThemeTranslation[];
  dayThemesMap: { [key: string]: BrandDayTheme };
  dayThemeTranslationsMap: { [key: string]: DayThemeTranslation };
}

interface DayThemeTranslationForm {
  dirty: boolean;
  transforms: DayThemeTransforms;
}

interface ChangeDayThemeFormAction {
  active?: boolean;
  valueChange?: { key: keyof DayThemeTranslatable; value: string };
}

type DayThemeTranslationsPageProps = RouteComponentProps;

export default function DayThemeTranslations(props: DayThemeTranslationsPageProps) {
  const [loading, setLoading] = useState(true);
  const [dataBundle, setDataBundle] = useState<DayThemeTranslationDataBundle | undefined>(
    undefined
  );
  const [activeDayTheme, setActiveDayTheme] = useState<BrandDayTheme | undefined>(undefined);
  const [dayThemeTranslationForm, setDayThemeTranslationForm] = useState<
    DayThemeTranslationForm | undefined
  >(undefined);
  const [search, setSearch] = useState('');

  const contentRef = useRef<HTMLDivElement | null>(null);

  const { activeProperty } = useActiveProperty();

  const serverInfo = useServerInfo();
  const { brand, languages } = serverInfo;
  const { user } = useUserInfo();

  useEffect(() => {
    setLoading(true);
    fetchDataBundle().catch(console.error);
  }, [activeProperty]);

  // this will refresh the currently active day theme if data bundle refreshes
  useEffect(() => {
    if (!dataBundle) return;

    setActiveDayTheme(state => {
      const brandDayTheme = dataBundle.dayThemes[0];
      if (!state) return brandDayTheme;
      const refreshedDayTheme = dataBundle.dayThemesMap[state._id];
      return refreshedDayTheme || brandDayTheme;
    });
  }, [dataBundle]);

  // this will reset the form each time a new day theme is clicked
  useEffect(() => {
    resetDayThemesForm();
  }, [activeDayTheme]);

  // this will scroll the page to top when search is modified
  useEffect(() => {
    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [search, contentRef]);

  const fuse = useMemo(() => {
    if (!dataBundle) return undefined;

    return new Fuse(dataBundle.dayThemes, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name'],
    });
  }, [dataBundle]);

  const fuseSearchResults = useMemo(() => {
    if (!search || !fuse) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const filteredDayThemes = !dataBundle ? [] : fuseSearchResults || dataBundle.dayThemes;

  async function fetchDataBundle() {
    try {
      const dayThemes = brand.dayThemes;
      const dayThemeTranslations = await getTranslations(activeProperty._id, 'daytheme');

      const dayThemesMap = mapBy(dayThemes, '_id');
      const dayThemeTranslationsMap = mapBy(dayThemeTranslations, 'refers');

      setDataBundle({
        dayThemes,
        dayThemeTranslations,
        dayThemesMap,
        dayThemeTranslationsMap,
      });
    } finally {
      setLoading(false);
    }
  }

  function resetDayThemesForm() {
    const transforms = activeDayTheme
      ? makeDayThemeTranslationTransform(
          languages.map(l => l.key),
          dataBundle?.dayThemeTranslationsMap[activeDayTheme._id]
        )
      : undefined;

    if (!transforms) {
      setDayThemeTranslationForm(undefined);
      return;
    }

    const formData: DayThemeTranslationForm = {
      dirty: false,
      transforms,
    };

    setDayThemeTranslationForm(formData);
  }

  function changeDayThemeForm(lang: string, change: ChangeDayThemeFormAction) {
    if (!dayThemeTranslationForm) return;

    const updatedForm = { ...dayThemeTranslationForm, dirty: true };

    if (!hasOwnProperty(updatedForm.transforms, lang)) {
      return;
    }

    updatedForm.transforms[lang].modified = true;

    if (change.active !== undefined) updatedForm.transforms[lang].active = change.active;
    if (change.valueChange !== undefined) {
      updatedForm.transforms[lang].delta[change.valueChange.key] = change.valueChange.value;
    }

    setDayThemeTranslationForm(updatedForm);
  }

  async function handleSubmitLevelForm() {
    if (
      !dayThemeTranslationForm ||
      !activeDayTheme ||
      !dataBundle ||
      !canModifyBrandTranslation(user, serverInfo)
    )
      return;

    const translation = generateDefaultNewTranslation(activeDayTheme._id, 'daytheme');

    translation.transforms = dayThemeTranslationForm.transforms;

    await replaceTranslationByRefer(activeDayTheme._id, translation);
    await fetchDataBundle();
    setDayThemeTranslationForm({ ...dayThemeTranslationForm, dirty: false });
  }

  function headerFn() {
    return <TranslationsSubNavigation active="dayThemes" property={activeProperty} />;
  }

  function leftFn() {
    return (
      <>
        <div
          className="sticky flex bg-white border-b"
          style={{ top: 'calc(130px + 40px + 0.75rem)', height: '50px', zIndex: 3 }}
        >
          <BubbleInput
            type="text"
            block
            appearance="bare"
            placeholder="Search..."
            className="flex-grow"
            inputStyle={{ height: '49px' }}
            inputClassName="px-5"
            value={search}
            onValue={setSearch}
          />
        </div>
        <div style={{ paddingTop: '50px' }}>
          {filteredDayThemes.map(dayTheme => (
            <div
              key={dayTheme._id}
              className={`list-item px-5 py-3 cursor-pointer ${
                activeDayTheme && safeIdCompare(activeDayTheme._id, dayTheme._id) ? 'active' : ''
              }`}
              onClick={() => setActiveDayTheme(dayTheme)}
            >
              <div className="flex items-center">
                <div className="overflow-ellipsis">
                  <div className="list-item-name font-semibold">{dayTheme.name}</div>
                </div>
                <FontAwesomeIcon
                  icon="chevron-right"
                  className="list-item-arrow ml-auto pl-3 flex-shrink-0"
                />
              </div>
            </div>
          ))}
          {filteredDayThemes.length < 1 && (
            <div className="grid items-center justify-center italic text-smoke bg-eggshell px-3 py-2">
              No day theme found
            </div>
          )}
        </div>
      </>
    );
  }

  function rightFn({ selectedLanguages }: TranslationFilters) {
    return (
      <>
        {dataBundle && activeDayTheme && dayThemeTranslationForm && (
          <>
            <div style={{ marginBottom: '-1px' }}>
              {!canModifyBrandTranslation(user, serverInfo) && (
                <BubbleAlert className="mt-3 mx-3" variant="ocean">
                  This day theme cannot be modified unless you're in a brand instance and a brand
                  admin.
                </BubbleAlert>
              )}
              <TranslationBlock
                label="Name"
                value={activeDayTheme.name}
                languages={selectedLanguages}
                charCountFn={({ language }) => (
                  <span className="text-smoke-light">
                    ({dayThemeTranslationForm.transforms[language.key].delta.name.length})
                  </span>
                )}
              >
                {({ language }) => (
                  <div className="flex items-start">
                    <BubbleButton
                      inverse
                      variant={
                        !dayThemeTranslationForm.transforms[language.key].active ? 'smoke' : 'navy'
                      }
                      className="px-1-i pt-2 mr-2 leading-none"
                      onClick={() =>
                        changeDayThemeForm(language.key, {
                          active: !dayThemeTranslationForm.transforms[language.key].active,
                        })
                      }
                      icon={
                        dayThemeTranslationForm.transforms[language.key].active
                          ? 'eye'
                          : 'eye-slash'
                      }
                    />
                    <BubbleInput
                      type="text"
                      block
                      className="flex-grow"
                      inputClassName={
                        !dayThemeTranslationForm.transforms[language.key].active
                          ? 'italic text-smoke'
                          : ''
                      }
                      value={dayThemeTranslationForm.transforms[language.key].delta.name}
                      onValue={val =>
                        changeDayThemeForm(language.key, {
                          valueChange: { key: 'name', value: val },
                        })
                      }
                    />
                  </div>
                )}
              </TranslationBlock>
            </div>

            <div className="sticky bottom-0 p-3 bg-eggshell border-t">
              <BubbleButton
                onClick={() => handleSubmitLevelForm()}
                variant="navy"
                disabled={
                  !dayThemeTranslationForm.dirty || !canModifyBrandTranslation(user, serverInfo)
                }
                icon="floppy-disk"
              >
                Save Changes
              </BubbleButton>
              <BubbleButton
                variant="slate"
                disabled={!dayThemeTranslationForm.dirty}
                inverse
                onClick={() => resetDayThemesForm()}
                icon="rotate-left"
              >
                Reset
              </BubbleButton>
            </div>
          </>
        )}
      </>
    );
  }

  // this adds a check to prevent closing tab if unsaved changes
  useEffect(() => {
    if (dayThemeTranslationForm && dayThemeTranslationForm.dirty) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  });

  return (
    <>
      <Prompt
        when={dayThemeTranslationForm && dayThemeTranslationForm.dirty}
        message="You have unsaved changes! Are you sure you want to leave?"
      />
      <Translations
        {...props}
        category="dayThemes"
        loading={loading}
        contentRef={contentRef}
        property={activeProperty}
        languages={languages}
        leftFn={leftFn}
        rightFn={rightFn}
        headerFn={headerFn}
      />
    </>
  );
}
